import type { CSSProperties, KeyboardEvent, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Box, Button, Stack, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { reorderIds } from "@shared/catalogue-order";
import { tokens } from "../../theme";

/** One screen filed under Information (Wi-Fi, VilMer ...). */
export interface ScreenEntry {
  id: string;
  name: string;
  /** What the status line says, and its dot. */
  status: { label: string; color: string };
}

/** One of the operator's own sections. */
export interface OwnEntry {
  id: string;
  name: string;
}

/**
 * Information's left column (NendaPortal_1 1689:14148): the standard sections,
 * then the operator's own - draggable - and anything else the template files
 * under Information.
 *
 * Selection keys: a screen by its id, an own section by `own:<id>` (see
 * {@link ownKey}) so a section id can never collide with a screen id.
 */
export default function InfoSectionList({
  mode,
  standard,
  own,
  more,
  selected,
  onSelect,
  onReorderOwn,
  onAddOwn,
}: {
  /** Which of Information's two pages this list is on. */
  mode: "own" | "standard";
  standard: ScreenEntry[];
  own: OwnEntry[];
  more: ScreenEntry[];
  selected?: string;
  onSelect: (key: string) => void;
  /** The own sections' ids in their new order. */
  onReorderOwn: (ids: string[]) => void;
  onAddOwn: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );
  const ids = own.map((o) => o.id);
  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over) return;
    const next = reorderIds(ids, String(active.id), String(over.id));
    if (next) onReorderOwn(next);
  };

  return (
    <Stack spacing={5} data-testid="info-section-list">
      {mode === "standard" && standard.length > 0 && (
        <Group
          title={t("infoSectionList.standardTitle", { count: standard.length })}
          subtitle={t("infoSectionList.standardSubtitle")}
          testId="info-standard-sections"
        >
          {standard.map((s) => (
            <Row
              key={s.id}
              label={s.name}
              selected={selected === s.id}
              onSelect={() => onSelect(s.id)}
              trailing={<Status {...s.status} />}
              testId={`info-section-${s.id}`}
            />
          ))}
        </Group>
      )}

      {mode === "own" && (
        <Group
          title={t("infoSectionList.ownTitle", { count: own.length })}
          subtitle={t("infoSectionList.ownSubtitle")}
          testId="info-own-sections"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              {own.map((o) => (
                <OwnRow
                  key={o.id}
                  entry={o}
                  selected={selected === ownKey(o.id)}
                  onSelect={() => onSelect(ownKey(o.id))}
                />
              ))}
            </SortableContext>
          </DndContext>
          <Button
            variant="outlined"
            endIcon={<AddIcon />}
            onClick={onAddOwn}
            sx={{
              mt: 2.5,
              textTransform: "none",
              fontWeight: "fontWeightSemibold",
            }}
            fullWidth
            data-testid="info-add-section"
          >
            {t("infoSectionList.addSection")}
          </Button>
        </Group>
      )}

      {mode === "standard" && more.length > 0 && (
        <Group
          title={t("infoSectionList.moreTitle")}
          subtitle={t("infoSectionList.moreSubtitle")}
          testId="info-more-sections"
        >
          {more.map((s) => (
            <Row
              key={s.id}
              label={s.name}
              selected={selected === s.id}
              onSelect={() => onSelect(s.id)}
              trailing={<Status {...s.status} />}
              testId={`info-section-${s.id}`}
            />
          ))}
        </Group>
      )}
    </Stack>
  );
}

/** The selection key of an own section. */
export const ownKey = (id: string) => `own:${id}`;

function Group({
  title,
  subtitle,
  testId,
  children,
}: {
  title: string;
  subtitle?: string;
  testId: string;
  children: ReactNode;
}) {
  return (
    <Box data-testid={testId}>
      <Typography
        component="h3"
        sx={{ typography: "title", fontWeight: "fontWeightSemibold" }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" sx={{ mt: 1, color: tokens.inkMuted }}>
          {subtitle}
        </Typography>
      )}
      <Stack spacing={0.75} sx={{ mt: 2 }}>
        {children}
      </Stack>
    </Box>
  );
}

function Status({ label, color }: { label: string; color: string }) {
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} sx={{ ml: 1 }}>
      <Box
        aria-hidden
        sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }}
      />
      <Typography
        variant="bodySm"
        sx={{ color: tokens.inkMuted, whiteSpace: "nowrap" }}
      >
        {label}
      </Typography>
    </Stack>
  );
}

const rowSx = (selected: boolean) => ({
  display: "flex",
  alignItems: "center",
  gap: 1.25,
  minHeight: 50,
  pl: 2.5,
  pr: 1.25,
  borderRadius: "8px",
  cursor: "pointer",
  bgcolor: selected ? tokens.brandTintSoft : tokens.surface,
  "&:hover": { bgcolor: tokens.brandTintSoft },
  "&:focus-visible": {
    outline: `2px solid ${tokens.brand}`,
    outlineOffset: 1,
  },
});

const labelSx = {
  flex: 1,
  minWidth: 0,
  fontSize: 16,
  fontWeight: "fontWeightSemibold",
  color: tokens.ink,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

function selectKeys(onSelect: () => void) {
  return (e: KeyboardEvent) => {
    if (e.target !== e.currentTarget) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onSelect();
    }
  };
}

function Row({
  label,
  selected,
  onSelect,
  trailing,
  testId,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  trailing?: ReactNode;
  testId: string;
}) {
  return (
    <Box
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      onKeyDown={selectKeys(onSelect)}
      sx={rowSx(selected)}
      data-testid={testId}
    >
      <Typography sx={labelSx}>{label}</Typography>
      {trailing}
    </Box>
  );
}

function OwnRow({
  entry,
  selected,
  onSelect,
}: {
  entry: OwnEntry;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation(["templates", "common"]);
  const s = useSortable({ id: entry.id });
  const style: CSSProperties = {
    transform: CSS.Translate.toString(s.transform),
    transition: s.transition,
    opacity: s.isDragging ? 0.6 : undefined,
    position: "relative",
    zIndex: s.isDragging ? 1 : undefined,
  };
  return (
    <Box
      ref={s.setNodeRef}
      style={style}
      role="button"
      tabIndex={0}
      aria-current={selected ? "true" : undefined}
      onClick={onSelect}
      onKeyDown={selectKeys(onSelect)}
      sx={rowSx(selected)}
      data-testid={`info-own-${entry.id}`}
    >
      <Box
        {...s.attributes}
        {...s.listeners}
        aria-label={t("startNavLayout.drag", { name: entry.name })}
        onClick={(e) => e.stopPropagation()}
        sx={{ display: "flex", color: tokens.ink, cursor: "grab" }}
        data-testid={`info-own-drag-${entry.id}`}
      >
        <DragIndicatorIcon />
      </Box>
      <Typography sx={labelSx}>{entry.name}</Typography>
    </Box>
  );
}
